import logger from "@/lib/logger";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CHARGER_SPECS, BODY_TYPES, MONTHS, YEARS } from "@/pages/NewJob";
import * as XLSX from "xlsx";
import {
  Zap, Plus, Printer, Search, ChevronLeft, ChevronRight,
  Package, Loader2, Tag, X, Upload, Building2, FileSpreadsheet,
  ShieldCheck, History, AlertTriangle, CheckCircle2, Clock, User, Phone,
  Download, Trash2, CircuitBoard
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  "New":        "bg-green-50 text-green-700 border-green-200",
  "Sold":       "bg-blue-50 text-blue-700 border-blue-200",
  "In Service": "bg-amber-50 text-amber-700 border-amber-200",
  "Repaired":   "bg-purple-50 text-purple-700 border-purple-200",
};
const STATUS_EMOJI = { "New":"✅","Sold":"💰","In Service":"🔧","Repaired":"♻️" };
const ALL_STATUSES = ["New","Sold","In Service","Repaired"];
const WARRANTY_STYLES = {
  "active":       { cls:"bg-emerald-50 text-emerald-700 border-emerald-200", label:"Active", icon:"🟢" },
  "expiring_soon":{ cls:"bg-amber-50 text-amber-700 border-amber-200",   label:"Expiring Soon", icon:"🟡" },
  "expired":      { cls:"bg-rose-50 text-rose-700 border-rose-200",      label:"Expired", icon:"🔴" },
  "no_warranty":  { cls:"bg-slate-100 text-slate-600 border-slate-200",  label:"No Warranty", icon:"⚪" },
};
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—";
const daysLeft = (expiry) => {
  if (!expiry) return null;
  const diff = Math.ceil((new Date(expiry) - new Date()) / (1000*60*60*24));
  return diff;
};

export default function Inventory() {
  const now = new Date();

  // ── Inventory list ──
  const [activeTab, setActiveTab] = useState("inventory");
  const [items,     setItems]     = useState([]);
  const [total,     setTotal]     = useState(0);
  const [pages,     setPages]     = useState(1);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [searchIn,  setSearchIn]  = useState("");
  const [stFilter,  setStFilter]  = useState("all");
  const [spFilter,  setSpFilter]  = useState("all");
  const [selected,  setSelected]  = useState(new Set());
  const [stats,     setStats]     = useState({});

  // ── Generate form ──
  const [showGen,     setShowGen]    = useState(false);
  const [qty,         setQty]        = useState(100);
  const [genSpec,     setGenSpec]    = useState("");
  const [genModel,    setGenModel]   = useState("");
  const [genBody,     setGenBody]    = useState("");
  const [genNotes,    setGenNotes]   = useState("");
  const [genParty,    setGenParty]   = useState("");
  const [startNum,    setStartNum]   = useState("");
  const [genMonth,    setGenMonth]   = useState(now.getMonth()+1);
  const [genYear,     setGenYear]    = useState(now.getFullYear());
  const [generating,  setGenerating] = useState(false);

  // ── Import from Excel ──
  const [showImport,  setShowImport]  = useState(false);
  const [importText,  setImportText]  = useState("");
  const [importSpec,  setImportSpec]  = useState("");
  const [importModel, setImportModel] = useState("");
  const [importBody,  setImportBody]  = useState("");
  const [importParty, setImportParty] = useState("");
  const [importMonth, setImportMonth] = useState(now.getMonth()+1);
  const [importYear,  setImportYear]  = useState(now.getFullYear());
  const [importing,   setImporting]   = useState(false);

  // ── Export to Excel ──
  const [exporting,    setExporting]    = useState(false);

  // ── Bulk Delete ──
  const [showDelConfirm, setShowDelConfirm] = useState(false);
  const [deleting,       setDeleting]       = useState(false);

  // ── Sold / Warranty dialog ──
  const [showSell,  setShowSell]  = useState(false);
  const [sellItem,  setSellItem]  = useState(null);   // {id}
  const [sellForm,  setSellForm]  = useState({ customer_name:"", customer_phone:"", sale_date: now.toISOString().slice(0,10), warranty_months:"12" });
  const [savingSell,setSavingSell]= useState(false);

  // ── Dispatch History ──
  const [dispatches,  setDispatches]  = useState([]);
  const [loadingDH,   setLoadingDH]   = useState(false);

  // ── Warranty Register ──
  const [warranty,    setWarranty]    = useState(null);
  const [loadingW,    setLoadingW]    = useState(false);

  // ─────────────────────────────────────────────
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit:50 };
      if (stFilter!=="all") params.status = stFilter;
      if (spFilter!=="all") params.charger_spec = spFilter;
      if (search.trim()) params.search = search;
      const res = await api.get(`/inventory`, { params });
      setItems(res.data.items||[]); setTotal(res.data.total||0); setPages(res.data.pages||1);
      setSelected(new Set());
    } catch (err) {
      logger.error("[Inventory] fetchItems failed:", err?.message);
      toast.error("Failed to load inventory");
    } finally { setLoading(false); }
  }, [page, stFilter, spFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get(`/inventory/stats`);
      setStats(r.data.by_status || {});
    } catch (err) {
      logger.error("[Inventory] fetchStats failed:", err?.message);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // api is a stable module-level import

  const fetchDispatches = useCallback(async () => {
    setLoadingDH(true);
    try {
      const r = await api.get(`/inventory/dispatch-history`);
      setDispatches(r.data.dispatches || []);
    } catch (err) {
      logger.error("[Inventory] fetchDispatches failed:", err?.message);
    } finally { setLoadingDH(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWarranty = useCallback(async () => {
    setLoadingW(true);
    try {
      const r = await api.get(`/inventory/warranty`);
      setWarranty(r.data);
    } catch (err) {
      logger.error("[Inventory] fetchWarranty failed:", err?.message);
    } finally { setLoadingW(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStats(); }, [items]); // re-fetch stats when items change
  useEffect(() => { if (activeTab==="dispatch") fetchDispatches(); }, [activeTab, fetchDispatches]);
  useEffect(() => { if (activeTab==="warranty") fetchWarranty();   }, [activeTab, fetchWarranty]);

  // ─── Handlers ────────────────────────────────

  const exportToExcel = async () => {
    setExporting(true);
    try {
      // Fetch ALL records (no pagination limit)
      const res = await api.get(`/inventory`, { params: { limit: 10000, page: 1 } });
      const records = res.data.items || [];

      if (!records.length) { toast.error("No records to export"); return; }

      // Build rows — every column shown on screen
      const rows = records.map((item, idx) => ({
        "Sr No":           idx + 1,
        "Barcode":         item.barcode         || "",
        "Serial Number":   typeof item.serial_number === "number"
                             ? item.serial_number
                             : (item.serial_number || ""),
        "Battery Cut-off / Ampere": item.charger_spec  || "",
        "Model":           item.model           || "",
        "Body Type":       item.body_type        || "",
        "Party / Customer":item.party_name       || "",
        "Status":          item.status           || "",
        "Purchase Month":  item.month ? MONTHS[item.month - 1] : "",
        "Purchase Year":   item.year             || "",
        "Generated Date":  item.created_at ? item.created_at.slice(0, 10) : "",
      }));

      // Create worksheet + workbook
      const ws = XLSX.utils.json_to_sheet(rows);

      // Auto-fit column widths based on content
      const colWidths = Object.keys(rows[0]).map(key => ({
        wch: Math.max(key.length, ...rows.map(r => String(r[key] || "").length)) + 2
      }));
      ws["!cols"] = colWidths;

      // Bold header row
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let C = range.s.c; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c: C });
        if (ws[addr]) {
          ws[addr].s = { font: { bold: true }, fill: { fgColor: { rgb: "16A34A" } } };
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Parts Records");

      const fileName = `XSTRONG_Parts_Records_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(`✅ ${records.length} records exported to ${fileName}`);
    } catch (err) {
      logger.error("[Inventory] exportToExcel failed:", err?.message);
      toast.error("Export failed. Please try again.");
    } finally { setExporting(false); }
  };
  const toggleSelect = (id) => setSelected(p => { const n=new Set(p); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleAll   = () => { if (selected.size===items.length) setSelected(new Set()); else setSelected(new Set(items.map(i=>i.id))); };
  const printSelected = () => { const ids=items.filter(i=>selected.has(i.id)).map(i=>i.barcode).join(","); window.open(`/barcode-labels?barcodes=${ids}`,"_blank"); };

  const deleteSelected = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`/inventory/bulk`, { data: { ids: [...selected] } });
      toast.success(`Deleted ${res.data.deleted} record${res.data.deleted !== 1 ? "s" : ""}`);
      setSelected(new Set()); setShowDelConfirm(false); fetchItems(); fetchStats();
    } catch (err) {
      logger.error("[Inventory] deleteSelected:", err?.message);
      toast.error("Failed to delete records");
    } finally { setDeleting(false); }
  };

  const handleStatusChange = (id, newStatus) => {
    if (newStatus === "Sold") {
      setSellItem({ id });
      setSellForm({ customer_name:"", customer_phone:"", sale_date: now.toISOString().slice(0,10), warranty_months:"12" });
      setShowSell(true);
    } else {
      updateStatus(id, newStatus);
    }
  };

  const updateStatus = async (id, status, extra={}) => {
    try {
      const updated = await api.patch(`/inventory/${id}/status`, { status, ...extra });
      setItems(p => p.map(i => i.id===id ? { ...i, ...updated.data } : i));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Failed to update"); }
  };

  const confirmSell = async () => {
    setSavingSell(true);
    try {
      await updateStatus(sellItem.id, "Sold", {
        customer_name:   sellForm.customer_name,
        customer_phone:  sellForm.customer_phone,
        sale_date:       sellForm.sale_date,
        warranty_months: parseInt(sellForm.warranty_months)||12,
      });
      setShowSell(false);
    } finally { setSavingSell(false); }
  };

  const generate = async () => {
    if (!genSpec) { toast.error("Please select a charger spec"); return; }
    setGenerating(true);
    try {
      const payload = { quantity:qty, charger_spec:genSpec, model:genModel, body_type:genBody, notes:genNotes, month:genMonth, year:genYear, party_name:genParty };
      if (startNum && !isNaN(parseInt(startNum))) payload.start_number = parseInt(startNum);
      const res = await api.post(`/inventory/generate`, payload);
      toast.success(`✅ ${res.data.generated} barcodes! ${res.data.start} → ${res.data.end}`);
      setShowGen(false); fetchItems(); fetchStats(); fetchDispatches();
    } catch { toast.error("Failed to generate"); }
    finally { setGenerating(false); }
  };

  const parseImport = (text) => text.trim().split("\n").filter(Boolean).map(line => {
    const parts = line.split(/[\t,]/).map(p=>p.trim()).filter(Boolean);
    return { serial_number:parts[0]||"", charger_spec:parts[1]||"", model:parts[2]||"", body_type:parts[3]||"" };
  }).filter(p=>p.serial_number);
  const importParsed = parseImport(importText);

  const doImport = async () => {
    if (!importParsed.length) { toast.error("No serial numbers detected"); return; }
    setImporting(true);
    try {
      const res = await api.post(`/inventory/import`, { items:importParsed, party_name:importParty, month:importMonth, year:importYear, charger_spec:importSpec, model:importModel, body_type:importBody });
      toast.success(`✅ ${res.data.imported} imported! ${res.data.start} → ${res.data.end}`);
      setShowImport(false); setImportText(""); fetchItems(); fetchStats(); fetchDispatches();
    } catch { toast.error("Import failed"); }
    finally { setImporting(false); }
  };

  const totalAll = Object.values(stats).reduce((a,b)=>a+b,0);

  // ─── Calculate warranty expiry preview ───────
  const previewExpiry = () => {
    try {
      const d = new Date(sellForm.sale_date);
      d.setMonth(d.getMonth() + parseInt(sellForm.warranty_months||12));
      return d.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
    } catch { return "—"; }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* ─── Header ─────────────────────────────── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Charger Inventory</h1>
          <p className="text-sm text-slate-500 mt-0.5">Generate, track and manage every charger unit</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.size>0 && (
            <>
              <Button variant="outline" size="sm" className="h-8 text-xs border-blue-200 text-blue-700 hover:bg-blue-50" onClick={printSelected}>
                <Printer size={13} className="mr-1.5"/>Print Labels ({selected.size})
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs border-rose-200 text-rose-600 hover:bg-rose-50"
                onClick={() => setShowDelConfirm(true)} data-testid="inventory-bulk-delete-button">
                <Trash2 size={13} className="mr-1.5"/>Delete Selected ({selected.size})
              </Button>
            </>
          )}
          <Button variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={exportToExcel} disabled={exporting} data-testid="inventory-export-btn">
            {exporting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting...</>
              : <><Download className="w-4 h-4 mr-2" />Export Excel</>}
          </Button>
          <Button variant="outline" className="border-green-200 text-green-700 hover:bg-green-50" onClick={()=>setShowImport(true)} data-testid="inventory-import-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2"/>Import Excel
          </Button>
          <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={()=>setShowGen(true)} data-testid="inventory-generate-btn">
            <Plus className="w-4 h-4 mr-2"/>Generate Barcodes
          </Button>
        </div>
      </div>

      {/* ─── Stats ───────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <Card className="rounded-xl border border-slate-200 bg-white col-span-2 sm:col-span-1">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>{totalAll.toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-0.5">Total Units</p>
          </CardContent>
        </Card>
        {ALL_STATUSES.map(s=>(
          <Card key={s} className={`rounded-xl border cursor-pointer transition-shadow hover:shadow-sm ${stFilter===s?"border-green-400 shadow-sm":"border-slate-200"} bg-white`}
            onClick={()=>{ setStFilter(stFilter===s?"all":s); setPage(1); setActiveTab("inventory"); }}>
            <CardContent className="p-4 text-center">
              <p className="text-xl font-bold" style={{fontFamily:"'Space Grotesk',sans-serif"}}>{STATUS_EMOJI[s]} {stats[s]??0}</p>
              <p className="text-xs text-slate-400 mt-0.5">{s}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ─── Tabs ────────────────────────────────── */}
      <div className="flex gap-1 mb-5 border-b border-slate-200">
        {[
          { id:"inventory",  label:"Inventory",        icon:Package },
          { id:"dispatch",   label:"Dispatch History",  icon:History },
          { id:"warranty",   label:"Warranty Register", icon:ShieldCheck },
        ].map(tab=>(
          <button key={tab.id} onClick={()=>setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab===tab.id ? "border-green-600 text-green-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <tab.icon size={15}/>{tab.label}
          </button>
        ))}
        <Link to="/inventory/pcb"
          className="ml-auto flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-green-700 hover:text-green-800 -mb-px"
          data-testid="inventory-pcb-link">
          <CircuitBoard size={15}/>PCB Replacement →
        </Link>
      </div>

      {/* ═══════════════ TAB 1: INVENTORY ═══════════════ */}
      {activeTab==="inventory" && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
              <Input placeholder="Search barcode, model, spec, party..."
                value={searchIn} onChange={e=>setSearchIn(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter"){ setSearch(searchIn); setPage(1); } }}
                className="pl-9 h-9 text-sm bg-white"/>
            </div>
            <Select value={spFilter} onValueChange={v=>{setSpFilter(v);setPage(1);}}>
              <SelectTrigger className="w-44 h-9 text-sm bg-white"><SelectValue placeholder="All Specs"/></SelectTrigger>
              <SelectContent><SelectItem value="all">All Specs</SelectItem>{CHARGER_SPECS.map(s=><SelectItem key={s} value={s}><span className="font-mono text-xs">{s}</span></SelectItem>)}</SelectContent>
            </Select>
            {(search||stFilter!=="all"||spFilter!=="all")&&(
              <Button variant="outline" size="sm" className="h-9 text-xs" onClick={()=>{setSearch("");setSearchIn("");setStFilter("all");setSpFilter("all");setPage(1);}}>
                <X size={13} className="mr-1"/>Clear
              </Button>
            )}
          </div>

          <Card className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <th className="px-4 py-3 w-10"><Checkbox checked={items.length>0&&selected.size===items.length} onCheckedChange={toggleAll}/></th>
                    {["Barcode","Spec","Model","Body","Party","Status","Date",""].map(h=>(
                      <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-3 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading?([...Array(5)].map((_,i)=>(
                    <tr key={i}>{[...Array(9)].map((_,j)=><td key={j} className="px-3 py-3"><Skeleton className="h-4"/></td>)}</tr>
                  ))):items.length===0?(
                    <tr><td colSpan={9} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Tag className="w-10 h-10 text-slate-300"/>
                        <p className="text-sm font-medium text-slate-700">No chargers yet</p>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={()=>setShowGen(true)}><Plus size={13} className="mr-1.5"/>Generate Barcodes</Button>
                      </div>
                    </td></tr>
                  ):items.map(item=>(
                    <tr key={item.id} className={`border-b border-slate-50 hover:bg-slate-50/60 transition-colors ${selected.has(item.id)?"bg-green-50/40":""}`}>
                      <td className="px-4 py-2.5" onClick={e=>e.stopPropagation()}><Checkbox checked={selected.has(item.id)} onCheckedChange={()=>toggleSelect(item.id)}/></td>
                      <td className="px-3 py-2.5"><span className="font-mono text-sm font-bold text-green-700">{item.barcode}</span></td>
                      <td className="px-3 py-2.5"><span className="font-mono text-xs font-semibold">{item.charger_spec||"—"}</span></td>
                      <td className="px-3 py-2.5"><span className="text-sm text-slate-600">{item.model||"—"}</span></td>
                      <td className="px-3 py-2.5"><span className="text-sm text-slate-600">{item.body_type||"—"}</span></td>
                      <td className="px-3 py-2.5">
                        {item.party_name ? <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">🏢 {item.party_name}</span> : <span className="text-xs text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <Select value={item.status} onValueChange={v=>handleStatusChange(item.id,v)}>
                          <SelectTrigger className={`h-7 text-xs border font-medium w-32 ${STATUS_STYLES[item.status]||""}`}><SelectValue/></SelectTrigger>
                          <SelectContent>{ALL_STATUSES.map(s=><SelectItem key={s} value={s}>{STATUS_EMOJI[s]} {s}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2.5"><p className="text-xs text-slate-400">{MONTH_NAMES[(item.month||1)-1]} {item.year}</p></td>
                      <td className="px-3 py-2.5">
                        <a href={`/barcode-labels?barcodes=${item.barcode}`} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-green-600" title="Print label"><Printer size={13}/></Button>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pages>1&&(
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <p className="text-xs text-slate-500">{total.toLocaleString()} total</p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={page===1} onClick={()=>setPage(p=>p-1)}><ChevronLeft size={13}/></Button>
                  <span className="text-xs text-slate-600 px-2">Page {page} of {pages}</span>
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={page===pages} onClick={()=>setPage(p=>p+1)}><ChevronRight size={13}/></Button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ═══════════════ TAB 2: DISPATCH HISTORY ═══════════════ */}
      {activeTab==="dispatch" && (
        <Card className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>All Dispatch Batches</h2>
            <p className="text-xs text-slate-400">{dispatches.length} batch{dispatches.length!==1?"es":""}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  {["Party / Customer","Spec","Body","Qty","Month/Year","Barcode Range",""].map(h=>(
                    <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadingDH?([...Array(4)].map((_,i)=>(
                  <tr key={i}>{[...Array(7)].map((_,j)=><td key={j} className="px-4 py-3"><Skeleton className="h-4"/></td>)}</tr>
                ))):dispatches.length===0?(
                  <tr><td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm text-slate-500">No dispatches yet. Generate barcodes with a Party Name to see batches here.</p>
                  </td></tr>
                ):dispatches.map((d, i)=>(
                  <tr key={`${d.party_name}-${d.year}-${d.month}-${i}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <p className="text-sm font-bold text-blue-700 flex items-center gap-1"><Building2 size={13}/>{d.party_name}</p>
                    </td>
                    <td className="px-4 py-3"><span className="font-mono text-sm font-semibold text-green-700">{d.charger_spec||"—"}</span></td>
                    <td className="px-4 py-3"><span className="text-sm text-slate-600">{d.body_type||"—"}</span></td>
                    <td className="px-4 py-3">
                      <span className="text-lg font-bold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>{d.count}</span>
                      <span className="text-xs text-slate-400 ml-1">units</span>
                    </td>
                    <td className="px-4 py-3"><span className="text-sm text-slate-600">{MONTH_NAMES[(d.month||1)-1]} {d.year}</span></td>
                    <td className="px-4 py-3">
                      <p className="font-mono text-xs text-green-700 font-semibold">{d.first_barcode}</p>
                      <p className="font-mono text-xs text-slate-400">→ {d.last_barcode}</p>
                    </td>
                    <td className="px-4 py-3">
                      <a href={`/barcode-labels?barcodes=${Array.from({length:d.count},(_,k)=>{const sn=parseInt(d.first_barcode.split("-").pop())+k; return `XSP-${d.year}${String(d.month).padStart(2,"0")}-${String(sn).padStart(4,"0")}`;}).join(",")}`}
                        target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50">
                          <Printer size={12} className="mr-1"/>Print {d.count}
                        </Button>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ═══════════════ TAB 3: WARRANTY REGISTER ═══════════════ */}
      {activeTab==="warranty" && (
        <>
          {/* Stats */}
          {warranty && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { k:"active",       label:"Active",        color:"text-emerald-700", bg:"bg-emerald-50 border-emerald-200", icon:"🟢" },
                { k:"expiring_soon",label:"Expiring Soon", color:"text-amber-700",   bg:"bg-amber-50 border-amber-200",     icon:"🟡" },
                { k:"expired",      label:"Expired",       color:"text-rose-700",    bg:"bg-rose-50 border-rose-200",       icon:"🔴" },
                { k:"no_warranty",  label:"No Warranty",   color:"text-slate-600",   bg:"bg-slate-50 border-slate-200",     icon:"⚪" },
              ].map(s=>(
                <Card key={s.k} className={`rounded-xl border ${s.bg}`}>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold" style={{fontFamily:"'Space Grotesk',sans-serif"}}>{s.icon} {warranty[s.k]??0}</p>
                    <p className={`text-xs mt-0.5 font-medium ${s.color}`}>{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Card className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Warranty Register — All Sold Chargers</h2>
              <p className="text-xs text-slate-400 mt-0.5">Chargers marked as Sold with warranty details</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    {["Barcode","Spec","Party","Customer","Sale Date","Expiry Date","Days Left","Status"].map(h=>(
                      <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-3 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingW?([...Array(4)].map((_,i)=>(
                    <tr key={i}>{[...Array(8)].map((_,j)=><td key={j} className="px-3 py-3"><Skeleton className="h-4"/></td>)}</tr>
                  ))):!warranty||warranty.items?.length===0?(
                    <tr><td colSpan={8} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <ShieldCheck className="w-10 h-10 text-slate-300"/>
                        <p className="text-sm text-slate-500">No sold chargers yet. Mark a charger as "Sold" to record warranty.</p>
                      </div>
                    </td></tr>
                  ):warranty.items.map((item, i)=>{
                    const ws = WARRANTY_STYLES[item.warranty_status||"no_warranty"];
                    const w  = item.warranty||{};
                    const dl = daysLeft(w.expiry_date);
                    return (
                      <tr key={item.id || item.barcode || i} className={`border-b border-slate-50 hover:bg-slate-50/60 ${item.warranty_status==="expired"?"bg-rose-50/20":item.warranty_status==="expiring_soon"?"bg-amber-50/30":""}`}>
                        <td className="px-3 py-3"><span className="font-mono text-sm font-bold text-green-700">{item.barcode}</span></td>
                        <td className="px-3 py-3"><span className="font-mono text-xs font-semibold">{item.charger_spec||"—"}</span></td>
                        <td className="px-3 py-3">{item.party_name?<span className="text-xs font-semibold text-blue-700">🏢 {item.party_name}</span>:<span className="text-xs text-slate-300">—</span>}</td>
                        <td className="px-3 py-3">
                          {w.customer_name ? (
                            <div>
                              <p className="text-sm font-medium text-slate-800">{w.customer_name}</p>
                              {w.customer_phone && <p className="text-xs text-slate-400">{w.customer_phone}</p>}
                            </div>
                          ) : <span className="text-xs text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-3"><span className="text-xs text-slate-600">{fmtDate(w.sale_date)}</span></td>
                        <td className="px-3 py-3"><span className="text-xs font-semibold text-slate-700">{fmtDate(w.expiry_date)}</span></td>
                        <td className="px-3 py-3">
                          {dl!==null ? (
                            <span className={`text-xs font-bold ${dl<0?"text-rose-600":dl<=30?"text-amber-600":"text-emerald-600"}`}>
                              {dl<0?`${Math.abs(dl)}d ago`:`${dl}d left`}
                            </span>
                          ) : <span className="text-xs text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${ws.cls}`}>{ws.icon} {ws.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* ═══ SOLD / WARRANTY DIALOG ═══ */}
      <Dialog open={showSell} onOpenChange={setShowSell}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"'Space Grotesk',sans-serif"}}>
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-blue-600"/>Mark as Sold + Warranty</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><User size={11}/>Customer / Buyer Name</Label>
              <Input className="mt-1.5 h-9 text-sm" placeholder="e.g. Litepage Technology" value={sellForm.customer_name} onChange={e=>setSellForm({...sellForm,customer_name:e.target.value})} data-testid="sell-customer-name"/>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><Phone size={11}/>Contact Number</Label>
              <Input className="mt-1.5 h-9 text-sm" placeholder="Phone number" value={sellForm.customer_phone} onChange={e=>setSellForm({...sellForm,customer_phone:e.target.value})}/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-700">Sale Date</Label>
                <Input type="date" className="mt-1.5 h-9 text-sm" value={sellForm.sale_date} onChange={e=>setSellForm({...sellForm,sale_date:e.target.value})}/>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Warranty (months)</Label>
                <Select value={sellForm.warranty_months} onValueChange={v=>setSellForm({...sellForm,warranty_months:v})}>
                  <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["3","6","12","18","24","36"].map(m=><SelectItem key={m} value={m}>{m} months</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-xs text-blue-700 font-medium">Warranty expires on: <strong>{previewExpiry()}</strong></p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={()=>setShowSell(false)}>Cancel</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={savingSell} onClick={confirmSell} data-testid="sell-confirm-btn">
              {savingSell?<Loader2 size={13} className="mr-1 animate-spin"/>:null} Confirm Sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ GENERATE MODAL ═══ */}
      {showGen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center"><Zap size={18} className="text-white"/></div>
                <div>
                  <h2 className="text-base font-bold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Generate Barcodes</h2>
                  <p className="text-xs text-slate-400">Each charger gets a unique barcode (XSP-YYYYMM-NNNN)</p>
                </div>
              </div>
              <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                <div>
                  <Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><Building2 size={12}/>Party / Customer Name</Label>
                  <Input placeholder="e.g. Litepage Technology" value={genParty} onChange={e=>setGenParty(e.target.value)} className="mt-1.5 h-9 text-sm font-semibold" data-testid="gen-party-input"/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Quantity <span className="text-rose-500">*</span></Label>
                    <Input type="number" min="1" max="500" value={qty} onChange={e=>setQty(parseInt(e.target.value)||1)} className="mt-1.5 h-9 text-sm font-semibold" data-testid="gen-qty-input"/>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Starting Serial</Label>
                    <Input type="number" min="1" placeholder="Auto" value={startNum} onChange={e=>setStartNum(e.target.value)} className="mt-1.5 h-9 text-sm font-mono" data-testid="gen-start-input"/>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Month</Label>
                    <Select value={String(genMonth)} onValueChange={v=>setGenMonth(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue/></SelectTrigger>
                      <SelectContent>{MONTHS.map((m,i)=><SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Year</Label>
                    <Select value={String(genYear)} onValueChange={v=>setGenYear(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue/></SelectTrigger>
                      <SelectContent>{YEARS.map(y=><SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700">Battery Cut-off / Ampere <span className="text-rose-500">*</span></Label>
                  <Select value={genSpec} onValueChange={setGenSpec}>
                    <SelectTrigger className="mt-1.5 h-9 text-sm font-semibold" data-testid="gen-spec-select"><SelectValue placeholder="Select charger spec"/></SelectTrigger>
                    <SelectContent>{CHARGER_SPECS.map(s=><SelectItem key={s} value={s}><span className="font-mono text-green-700">{s}</span></SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Model</Label>
                    <Input placeholder="e.g. XC-32" value={genModel} onChange={e=>setGenModel(e.target.value)} className="mt-1.5 h-9 text-sm"/>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Body Type</Label>
                    <Select value={genBody} onValueChange={setGenBody}>
                      <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue placeholder="Select"/></SelectTrigger>
                      <SelectContent>{BODY_TYPES.map(b=><SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-xs">
                  {genParty&&<p className="font-bold text-slate-800 mb-1">🏢 {genParty}</p>}
                  <p className="font-mono text-green-800 font-bold">XSP-{genYear}{String(genMonth).padStart(2,"0")}-{startNum?String(parseInt(startNum)||1).padStart(4,"0"):"0001"}</p>
                  <p className="text-slate-500 mt-0.5">Last: <span className="font-mono font-semibold text-green-700">XSP-{genYear}{String(genMonth).padStart(2,"0")}-{startNum?String((parseInt(startNum)||1)+qty-1).padStart(4,"0"):`${qty}`.padStart(4,"0")}</span></p>
                  {genSpec&&<p className="text-green-700 mt-0.5">⚡ {genSpec}{genBody?` · ${genBody}`:""}</p>}
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <Button variant="outline" className="flex-1" onClick={()=>setShowGen(false)}>Cancel</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={generate} disabled={generating||!genSpec}>
                  {generating?<><Loader2 size={14} className="mr-1.5 animate-spin"/>Generating...</>:<><Zap size={14} className="mr-1.5"/>Generate {qty}</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ IMPORT EXCEL MODAL ═══ */}
      {showImport && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center"><FileSpreadsheet size={18} className="text-white"/></div>
                <div>
                  <h2 className="text-base font-bold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Import from Excel / CSV</h2>
                  <p className="text-xs text-slate-400">Paste serial numbers directly from your spreadsheet</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><Building2 size={12}/>Party / Customer Name</Label>
                  <Input placeholder="e.g. Litepage Technology" value={importParty} onChange={e=>setImportParty(e.target.value)} className="mt-1.5 h-9 text-sm font-semibold"/>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Spec (default)</Label>
                    <Select value={importSpec} onValueChange={setImportSpec}>
                      <SelectTrigger className="mt-1.5 h-9 text-xs"><SelectValue placeholder="All"/></SelectTrigger>
                      <SelectContent>{CHARGER_SPECS.map(s=><SelectItem key={s} value={s}><span className="font-mono text-xs">{s}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Month</Label>
                    <Select value={String(importMonth)} onValueChange={v=>setImportMonth(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-9 text-xs"><SelectValue/></SelectTrigger>
                      <SelectContent>{MONTHS.map((m,i)=><SelectItem key={i+1} value={String(i+1)}>{m.slice(0,3)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Year</Label>
                    <Select value={String(importYear)} onValueChange={v=>setImportYear(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-9 text-xs"><SelectValue/></SelectTrigger>
                      <SelectContent>{YEARS.map(y=><SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700">Paste Serial Numbers <span className="text-rose-500">*</span></Label>
                  <Textarea
                    placeholder={"Paste from Excel — one per line:\n1001\n1002\n1003\n\nOr tab-separated:\n1001\t67.2V / 6A\tXC-32\tAluminium Body"}
                    value={importText} onChange={e=>setImportText(e.target.value)}
                    className="mt-1.5 text-sm font-mono resize-none" rows={5}
                    data-testid="import-textarea"
                  />
                  {importParsed.length>0&&<p className="text-xs text-green-600 mt-1 font-medium">✅ {importParsed.length} serial numbers detected</p>}
                </div>
                {importParsed.length>0&&(
                  <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-xs">
                    {importParty&&<p className="font-bold text-slate-800">🏢 {importParty}</p>}
                    <p className="font-mono text-green-800">XSP-{importYear}{String(importMonth).padStart(2,"0")}-{String(importParsed[0]?.serial_number).padStart(4,"0")}</p>
                    {importParsed.length>1&&<p className="font-mono text-green-600">→ XSP-{importYear}{String(importMonth).padStart(2,"0")}-{String(importParsed[importParsed.length-1]?.serial_number).padStart(4,"0")}</p>}
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-5">
                <Button variant="outline" className="flex-1" onClick={()=>{setShowImport(false);setImportText("");}}>Cancel</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={doImport} disabled={importing||!importParsed.length}>
                  {importing?<><Loader2 size={14} className="mr-1.5 animate-spin"/>Importing...</>:<><Upload size={14} className="mr-1.5"/>Import {importParsed.length}</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showDelConfirm} onOpenChange={setShowDelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} record{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{selected.size}</strong> charger record{selected.size !== 1 ? "s" : ""} from the inventory.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={deleteSelected}
              disabled={deleting} data-testid="inventory-bulk-delete-confirm">
              {deleting ? <><Loader2 size={13} className="mr-1.5 animate-spin"/>Deleting...</> : `Delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
